import * as React from 'react'
import { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react'
import { Button, Field, makeStyles, MessageBar, shorthands, tokens } from '@fluentui/react-components'
import { bundleIcon, DismissFilled, DismissRegular, MailArrowForwardFilled, MailArrowForwardRegular } from '@fluentui/react-icons'
import MessageContext, { mapOutlookItem } from '../../context/MessageContext'
import useEntity from '../../hooks/useEntity'
import useAxios from '../../hooks/useAxios'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { AxiosError, AxiosInstance } from 'axios'
import FileSuggestionDataGrid from './FileSuggestionDataGrid'
import { appInsightsReact } from '../../pages/App'
import { SeverityLevel } from '@microsoft/applicationinsights-common'
import useHost from '../../hooks/useHost'
import { useTranslation } from 'react-i18next'
import useUser from '../../hooks/useUser'
import FilePicker from '../TagPicker/FilePicker'

export enum FileMatchType {
    Reference = 'REFERENCE',
    Party = 'PARTY',
    TimeSuggestion = 'TIME_SUGGESTION',
    Conversation = 'CONVERSATION',
    Other = 'OTHER',
}

export type FileSuggestionMatchDto = {
    id: string
    reference: string
    name: string
    matchType: FileMatchType
}

const useStyles = makeStyles({
    alert: {
        display: 'grid',
    },
    container: {
        display: 'grid',
        gridTemplateRows: 'auto min-content',
        ...shorthands.gap(tokens.spacingVerticalS),
        marginTop: tokens.spacingVerticalM,
    },
    formRow: {
        display: 'grid',
        ...shorthands.gap(tokens.spacingVerticalS),
    },
    field: {
        alignItems: 'center',
        '> label': {
            ...shorthands.overflow('hidden'),
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        },
    },
    buttonContainer: {
        display: 'flex',
        ...shorthands.gap(tokens.spacingHorizontalL, tokens.spacingVerticalS),
        flexDirection: 'row',
        flexWrap: 'wrap',
        '> *': {
            display: 'flex',
            ...shorthands.gap(tokens.spacingVerticalS),
        },
    },
})

function submitForm(
    axios: AxiosInstance,
    messageId: string,
    messageRestId: string,
    userEmail: string,
    notificationEmail: string,
    hostKey: string,
    references: string[],
    setIsSubmitting: (value: boolean) => void,
    setIsProcessing: (value: boolean) => void,
    setError: (value: string) => void,
) {
    setIsSubmitting(true)
    axios
        .post<string>(
            `/api/file-message/`,
            { messageId, messageRestId, userEmail, notificationEmail: notificationEmail, hostKey, references, isBatch: false },
            { baseURL: process.env.INBOXER_HOST },
        )
        .then(() => {
            setIsSubmitting(false)
            setIsProcessing(true)
        })
        .catch((error: AxiosError) => {
            setError(error.message)
            setIsSubmitting(false)
            setIsProcessing(false)
            appInsightsReact.trackException({
                exception: error,
                severityLevel: SeverityLevel.Error,
            })
        })
}

const FileMessageForm: FunctionComponent = () => {
    const axios = useAxios()
    const host = useHost()
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const { isViewport, viewport } = useViewport()
    const orientation = useMemo(() => (isViewport('>=', Viewport.Large) ? 'horizontal' : 'vertical'), [viewport])
    const { currentMailbox, currentEmail } = useUser()
    const { outlookMessage, immutableId: messageId, itemId: messageRestId, fileIdMap } = useContext(MessageContext)
    const [references, setReferences] = useState<string[]>([])
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const [isProcessing, setIsProcessing] = useState<boolean>(false)
    const [error, setError] = useState<string>()

    const [suggestions, , resetSuggestions] = useEntity<FileSuggestionMatchDto[]>(
        () => ({
            method: 'POST',
            url: '/email/file-suggest',
            data: [mapOutlookItem(outlookMessage as Office.MessageRead)],
        }),
        messageId.loaded ? false : undefined,
        result => (result.data as FileSuggestionMatchDto[]).filter((value, index, array) => array.indexOf(value) === index),
        [],
    )

    useEffect(() => {
        if (messageId.rejected && suggestions.rejected) {
            appInsightsReact.trackException({
                exception: suggestions.error,
                severityLevel: SeverityLevel.Error,
                properties: { reactComponent: 'FileMessage.FileMessageForm', emailId: messageId },
            })
        }
    }, [suggestions])

    useEffect(() => {
        setReferences([])
        resetSuggestions()
    }, [messageId.data])

    useEffect(() => {
        setIsProcessing(false)
        resetSuggestions()
    }, [outlookMessage])

    useEffect(() => {
        if (fileIdMap) {
            setReferences([...fileIdMap.keys()])
        } else if (suggestions.data) {
            setReferences(
                suggestions.data
                    .filter(suggestion => [FileMatchType.Reference].includes(suggestion.matchType))
                    .map(suggestion => suggestion.reference),
            )
        }
    }, [fileIdMap, suggestions.data])

    const FileIconComponent = bundleIcon(MailArrowForwardFilled, MailArrowForwardRegular)
    const IgnoreIconComponent = bundleIcon(DismissFilled, DismissRegular)

    return (
        <div>
            <div className={styles.alert}>
                {error && (
                    <MessageBar intent={'error'} layout={'multiline'}>
                        {translate('email.filing.error.other', { error: error })}
                    </MessageBar>
                )}
                {!isProcessing && (!fileIdMap || fileIdMap.size === 0) && (
                    <MessageBar intent={'info'} layout={'multiline'}>
                        {translate('email.filing.file.description')}
                    </MessageBar>
                )}
                {isProcessing && (
                    <MessageBar intent={'info'} layout={'multiline'}>
                        {translate('email.filing.file.description')}
                    </MessageBar>
                )}
            </div>
            <div className={styles.container}>
                <FileSuggestionDataGrid suggestions={suggestions} selectedItems={references} onSelectionChange={setReferences} />

                <div className={styles.formRow}>
                    <Field label={translate('email.filing.file')} orientation={orientation} className={styles.field}>
                        <FilePicker
                            selectedOptions={references}
                            getEntityValue={entity => entity.reference}
                            onOptionsSelect={(_, selectedOptions) => setReferences(selectedOptions)}
                            filter={() => true}
                        />
                    </Field>
                </div>
                <div className={styles.buttonContainer}>
                    <Button
                        appearance="primary"
                        icon={<FileIconComponent />}
                        disabled={isSubmitting || isProcessing}
                        onClick={() => {
                            submitForm(
                                axios,
                                messageId.data,
                                messageRestId,
                                currentMailbox,
                                currentEmail,
                                host.RowKey,
                                references,
                                setIsSubmitting,
                                setIsProcessing,
                                setError,
                            )
                        }}
                    >
                        {translate('action.file')}
                    </Button>
                    <Button
                        icon={<IgnoreIconComponent />}
                        disabled={isSubmitting || isProcessing || references?.length > 0}
                        onClick={() => {
                            submitForm(
                                axios,
                                messageId.data,
                                messageRestId,
                                currentMailbox,
                                currentEmail,
                                host.RowKey,
                                [],
                                setIsSubmitting,
                                setIsProcessing,
                                setError,
                            )
                        }}
                    >
                        {translate('action.not_file')}
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default FileMessageForm
